import { audienceIdForPartnerRole, audienceIdForPartnerSegment } from '@/data/audienceUseCases';

export const creativePartnerSegments = [
  {
    id: 'influencers',
    title: 'Influencers',
    body: 'Turn brand stories into polished reels and serialized content without losing your voice.',
    badge: 'Social & Brand',
    perk: 'Brand Voice · Fast Social Exports',
    iconType: 'crown',
    accent: '#62FAE3',
    audienceUseCaseId: audienceIdForPartnerSegment('influencers'),
  },
  {
    id: 'creators',
    title: 'Creators',
    body: 'Batch shorts, explainers, and episodic posts with a scene-native timeline.',
    badge: 'Episodic Video',
    perk: 'Scene-Native Timeline · Multi-Aspect',
    iconType: 'layers',
    accent: '#A78BFA',
    audienceUseCaseId: audienceIdForPartnerSegment('creators'),
  },
  {
    id: 'storytellers',
    title: 'Storytellers',
    body: 'Draft narratives, generate shots per beat, and iterate with Director AI.',
    badge: 'Narrative & Script',
    perk: 'Beat-by-Beat Gen · Director AI Engine',
    iconType: 'feather',
    accent: '#2DD4BF',
    audienceUseCaseId: audienceIdForPartnerSegment('storytellers'),
  },
  {
    id: 'ai-moviemakers',
    title: 'AI Moviemakers',
    body: 'Ship cinematic experiments with Runway, Seedance, Veo, and multi-provider pipelines.',
    badge: 'Cinematic VFX',
    perk: 'Runway Gen-3 · Google Veo · Seedance',
    iconType: 'clapperboard',
    accent: '#FCD34D',
    audienceUseCaseId: audienceIdForPartnerSegment('ai-moviemakers'),
  },
  {
    id: 'ad-makers',
    title: 'Ad makers',
    body: 'Pitch concepts with real motion, swap providers per shot, and ship client cuts fast.',
    badge: 'Commercial Cuts',
    perk: 'Multi-Provider Swap · Quick Client Cuts',
    iconType: 'target',
    accent: '#FB7185',
    audienceUseCaseId: audienceIdForPartnerSegment('ad-makers'),
  },
  {
    id: 'podcasters',
    title: 'Podcasters',
    body: 'Repurpose episodes into visual clips, audiograms, and promo scenes.',
    badge: 'Audio to Video',
    perk: 'Audio-to-Scene · Animated Audiograms',
    iconType: 'headphones',
    accent: '#38BDF8',
    audienceUseCaseId: audienceIdForPartnerSegment('podcasters'),
  },
  {
    id: 'video-editors',
    title: 'Video editors',
    body: 'Layer AI-generated scenes into professional edits with precise subtitle control.',
    badge: 'Post-Production',
    perk: 'Word Timestamps · Subtitle Styling',
    iconType: 'sliders',
    accent: '#34D399',
    audienceUseCaseId: audienceIdForPartnerSegment('video-editors'),
  },
];

export const creativePartnerBenefits = {
  studioPerks: [
    'Expanded generation credits for partner projects',
    'Early access to new flows, models, and Director tools',
    'Co-marketing opportunities with Twinnee',
  ],
  showcase: [
    'Featured work on Twinnee channels and case studies',
    'Collaboration spotlights with the product team',
    'Priority feedback loop on roadmap features',
  ],
};

/** Runway Gen-3 Alpha clips for CPP showcase (official research CDN). */
export const creativePartnerShowcaseVideos = [
  {
    id: 'runway-train-reflection',
    title: 'Midnight Express',
    style: 'Cinematic · Narrative',
    videoUrl:
      'https://d3phaj0sisr2ct.cloudfront.net/site/videos/gen-3-alpha/carousel-01/gen-3-alpha-output-001.mp4',
  },
  {
    id: 'runway-astronaut-rio',
    title: 'Rio Runner',
    style: 'Action · Urban',
    videoUrl:
      'https://d3phaj0sisr2ct.cloudfront.net/site/videos/gen-3-alpha/carousel-01/gen-3-alpha-output-002.mp4',
  },
  {
    id: 'runway-underwater-fpv',
    title: 'Coral Suburb',
    style: 'Fantasy · FPV',
    videoUrl:
      'https://d3phaj0sisr2ct.cloudfront.net/site/videos/gen-3-alpha/carousel-01/gen-3-alpha-output-003.mp4',
  },
  {
    id: 'runway-balloon-night',
    title: 'Night Balloon',
    style: 'Mystery · Handheld',
    videoUrl:
      'https://d3phaj0sisr2ct.cloudfront.net/site/videos/gen-3-alpha/carousel-01/gen-3-alpha-output-004.mp4',
  },
  {
    id: 'runway-portrait-glow',
    title: 'Glow Portrait',
    style: 'Drama · Close-up',
    videoUrl:
      'https://d3phaj0sisr2ct.cloudfront.net/site/videos/gen-3-alpha/humans/gen-3-alpha-output-001.mp4',
  },
  {
    id: 'runway-portrait-pullback',
    title: 'Side Light',
    style: 'Portrait · Pull back',
    videoUrl:
      'https://d3phaj0sisr2ct.cloudfront.net/site/videos/gen-3-alpha/humans/gen-3-alpha-output-002.mp4',
  },
  {
    id: 'runway-gym-zoom',
    title: 'Empty Gym',
    style: 'Character · Zoom',
    videoUrl:
      'https://d3phaj0sisr2ct.cloudfront.net/site/videos/gen-3-alpha/humans/gen-3-alpha-output-003.mp4',
  },
  {
    id: 'runway-anime-ship',
    title: 'Harbor Song',
    style: 'Anime · Character',
    videoUrl:
      'https://d3phaj0sisr2ct.cloudfront.net/site/videos/gen-3-alpha/artists/gen-3-alpha-output-011.mp4',
  },
  {
    id: 'runway-creature-city',
    title: 'Night Walker',
    style: 'Sci-fi · Wide shot',
    videoUrl:
      'https://d3phaj0sisr2ct.cloudfront.net/site/videos/gen-3-alpha/artists/gen-3-alpha-output-001-v1.mp4',
  },
  {
    id: 'runway-oil-painting',
    title: 'Forest Parallax',
    style: 'Art · Oil painting',
    videoUrl:
      'https://d3phaj0sisr2ct.cloudfront.net/site/videos/gen-3-alpha/artists/gen-3-alpha-output-002.mp4',
  },
];

export const creativePartnerSteps = [
  { n: '01', title: 'Apply', body: 'Share your portfolio, channel, and the creative formats you produce.' },
  { n: '02', title: 'Review', body: 'Our partnership team reviews fit, audience reach, and production velocity.' },
  { n: '03', title: 'Onboard', body: 'Approved partners receive credits, early access, and showcase opportunities.' },
];

export const creativePartnerRoleOptions = [
  { value: 'influencer', label: 'Influencer / Content Creator', audienceUseCaseId: audienceIdForPartnerRole('influencer') },
  { value: 'creator', label: 'Video Creator / YouTuber', audienceUseCaseId: audienceIdForPartnerRole('creator') },
  { value: 'storyteller', label: 'Writer / Storyteller', audienceUseCaseId: audienceIdForPartnerRole('storyteller') },
  { value: 'ai_filmmaker', label: 'AI Filmmaker / Director', audienceUseCaseId: audienceIdForPartnerRole('ai_filmmaker') },
  { value: 'ad_maker', label: 'Commercial / Ad Creator', audienceUseCaseId: audienceIdForPartnerRole('ad_maker') },
  { value: 'podcaster', label: 'Podcaster / Audio Producer', audienceUseCaseId: audienceIdForPartnerRole('podcaster') },
  { value: 'video_editor', label: 'Video Editor / Post-Production', audienceUseCaseId: audienceIdForPartnerRole('video_editor') },
  { value: 'agency_lead', label: 'Agency / Studio Lead', audienceUseCaseId: audienceIdForPartnerRole('agency_lead') },
  { value: 'other', label: 'Other Creator', audienceUseCaseId: audienceIdForPartnerRole('other') },
];
